import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Canonical loading primitives. Replaces the ~500 ad-hoc
 * `<Loader2 className="animate-spin" />` scattered across the app so every
 * spinner shares the same size, color token and a11y label.
 *
 *   <LoadingSpinner />                     // inline icon (buttons, cells)
 *   <LoadingState label="Cargando…" />     // centered block (page / suspense)
 *   <LoadingState variant="overlay" />     // covers its relative parent
 */

type SpinnerSize = 'sm' | 'md' | 'lg';

const SPINNER_PX: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
};

interface LoadingSpinnerProps {
  size?: SpinnerSize;
  className?: string;
  'aria-label'?: string;
}

/** Bare spinning icon — use inside buttons, table cells, inline text. */
export function LoadingSpinner({ size = 'md', className, 'aria-label': ariaLabel = 'Cargando' }: LoadingSpinnerProps) {
  return (
    <Loader2
      role="status"
      aria-label={ariaLabel}
      // eslint-disable-next-line local/no-ad-hoc-loading-state -- este ES el spinner canonico
      className={cn('animate-spin text-muted-foreground', SPINNER_PX[size], className)}
    />
  );
}

interface LoadingStateProps {
  /** inline: spinner + optional label in a row. page: centered block. overlay: absolute cover. */
  variant?: 'inline' | 'page' | 'overlay';
  label?: string;
  size?: SpinnerSize;
  className?: string;
}

/** Loading block with optional label. Default `page` = centered, min-h 60vh. */
export function LoadingState({ variant = 'page', label, size, className }: LoadingStateProps) {
  if (variant === 'inline') {
    return (
      <span className={cn('inline-flex items-center gap-2 text-sm text-muted-foreground', className)}>
        <LoadingSpinner size={size ?? 'sm'} />
        {label}
      </span>
    );
  }

  const body = (
    <div className="flex flex-col items-center justify-center gap-3">
      <LoadingSpinner size={size ?? 'lg'} />
      {label && <p className="text-sm text-muted-foreground">{label}</p>}
    </div>
  );

  if (variant === 'overlay') {
    return (
      <div className={cn('absolute inset-0 z-10 flex items-center justify-center bg-background/70 backdrop-blur-sm', className)}>
        {body}
      </div>
    );
  }

  return (
    <div className={cn('flex min-h-[60vh] w-full items-center justify-center', className)}>
      {body}
    </div>
  );
}
